/**
 * A short, reportable description of a thrown value — and nothing more than that.
 *
 * **The reason this is a function rather than `String(error)` at each call site** is that the errors it
 * describes come back across the Capacitor bridge from calls whose arguments include a password.
 * Anything that serialises an error object wholesale risks carrying those arguments into a log, and
 * Capacitor already does exactly that at its own debug level: the bridge logs every plugin call's
 * `methodData`, so a sign-in's password is in `logcat` before any of our code runs. That is not this
 * function's doing and not something it can fix, but it is the reason it reads two named fields and
 * builds its own string instead of stringifying what it was handed.
 *
 * `code` is included because the biometric plugin's failures are told apart by it and the message alone
 * is often the same sentence for several of them.
 */
export function errorDetail(error: unknown): string {
  const { message, code } = (error ?? {}) as { message?: unknown; code?: unknown };
  const parts = [typeof message === 'string' && message ? message : null, code == null ? null : `code=${code}`];
  const detail = parts.filter(part => part !== null).join(' ');
  // Something was thrown, so the absence of both fields is itself worth reporting as a shape.
  return detail || 'no message';
}
